import React from 'react';

import useLocalStorage from '~/hooks/useLocalStorage';

import {
	type NavigationLayout,
	getNavigationMigrationConfig,
} from '~/config/navigationMigration';


// Use namespaced key to avoid conflicts with other localStorage keys
const STORAGE_KEY = 'navigation/migration/layout';

type NavigationLayoutContextValue = {
	currentLayout: NavigationLayout;
	setLayout: (layout: NavigationLayout) => void;
	showToggle: boolean;
};

const NavigationLayoutContext = React.createContext<NavigationLayoutContextValue | null>(null);

/**
 * Navigation Layout Context Provider
 *
 * Provides navigation layout state to all components that need it.
 * When persistLayoutPreference is true, uses localStorage. Otherwise uses in-memory state only.
 */
export const NavigationLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const config = getNavigationMigrationConfig();
	const [persistedPreference, setPersistedPreference] = useLocalStorage(
		STORAGE_KEY,
		null as NavigationLayout | null,
	);
	const [sessionPreference, setSessionPreference] = React.useState<NavigationLayout | null>(null);

	// When not persisting: use defaultLayout, sessionPreference only affects current session
	// When persisting: use persistedPreference, fall back to defaultLayout
	const currentLayout: NavigationLayout = React.useMemo(() => {
		if (!config.showToggle) {
			return config.defaultLayout;
		}

		if (config.persistLayoutPreference && persistedPreference !== null) {
			return persistedPreference;
		}

		if (!config.persistLayoutPreference && sessionPreference !== null) {
			return sessionPreference;
		}

		return config.defaultLayout;
	}, [config.showToggle, config.defaultLayout, config.persistLayoutPreference, persistedPreference, sessionPreference]);

	const setLayout = React.useCallback(
		(layout: NavigationLayout) => {
			if (config.persistLayoutPreference) {
				setPersistedPreference(layout);
			} else {
				setSessionPreference(layout);
			}
		},
		[config.persistLayoutPreference, setPersistedPreference],
	);

	const value: NavigationLayoutContextValue = React.useMemo(
		() => ({
			currentLayout,
			setLayout,
			showToggle: config.showToggle,
		}),
		[currentLayout, setLayout, config.showToggle],
	);

	return (
		<NavigationLayoutContext.Provider value={value}>
			{children}
		</NavigationLayoutContext.Provider>
	);
};

/**
 * Hook to use navigation layout context
 *
 * @throws Error if used outside NavigationLayoutProvider
 */
export function useNavigationLayoutContext(): NavigationLayoutContextValue {
	const context = React.useContext(NavigationLayoutContext);
	if (!context) {
		throw new Error('useNavigationLayoutContext must be used within NavigationLayoutProvider');
	}

	return context;
}

export default NavigationLayoutContext;
